import type { PlatformId } from "@/components/social-accounts/provider-icon";
import type { ToolPageKey } from "@/lib/seo";

/**
 * The free public tools, in one place.
 *
 * Three surfaces read this: the /tools index page, the `ItemList` schema that
 * page emits, and the drift test that fails when a `tools.*.tsx` route exists
 * without a card here. Adding a tool and forgetting the hub is the failure this
 * prevents — the hub is the only internal page linking them, so a missing card
 * means a page reachable from a footer link and nothing else.
 *
 * `key` ties each entry to its `PAGE_META` record, so the title and description
 * a card links to are never restated here and cannot drift from the ones the
 * page itself serves.
 */
export type ToolCard = {
  /** The `PAGE_META` key, and therefore the route's own title/description. */
  key: ToolPageKey;
  to: string;
  /** Short card name — the tool, not the SEO title. */
  name: string;
  /** The question someone actually types, phrased the way they type it. */
  answers: string;
  /** What it does, and what it refuses to guess at. */
  blurb: string;
  /** Networks it covers, rendered as brand marks on the card. */
  networks: ReadonlyArray<PlatformId>;
};

export const TOOL_CARDS: ReadonlyArray<ToolCard> = [
  {
    key: "characterCounter",
    to: "/tools/character-counter",
    name: "Character counter",
    answers: "Will this post fit on every network?",
    blurb:
      "Paste a post and see, network by network, whether it fits, counted the way each one counts: X charges 23 for any link and 2 per emoji, Bluesky counts what you see, YouTube counts bytes. It is the same code the PostDaily composer runs, in your browser.",
    networks: ["x", "instagram", "tiktok", "bluesky", "threads"],
  },
  {
    key: "contentCalendar2027",
    to: "/tools/content-calendar-2027",
    name: "2027 content calendar",
    answers: "What should I post about in 2027?",
    blurb:
      "Every date in 2027 worth planning a post around, from New Year's Day to Giving Tuesday, each with an idea to replace. Download it as a spreadsheet in the exact columns a bulk import reads, so a finished plan becomes a queue in one upload.",
    networks: ["instagram", "facebook", "tiktok", "x", "pinterest"],
  },
  {
    key: "bulkCsvChecker",
    to: "/tools/bulk-csv-checker",
    name: "Bulk CSV checker",
    answers: "Will my CSV upload without errors?",
    blurb:
      "Paste a bulk-scheduling CSV and every row is checked with the same code the import uses: dates, timezone, image links, and each network's character limit and image rule. Every problem comes with the fix, and the file never leaves your browser.",
    networks: ["instagram", "tiktok", "pinterest", "facebook", "x"],
  },
  {
    key: "threadSplitter",
    to: "/tools/thread-splitter",
    name: "Thread maker & splitter",
    answers: "How do I split this into a thread?",
    blurb:
      "Paste a draft and get numbered posts that fit. Each network is counted its own way — X charges a flat 23 for any link and 2 per emoji, Bluesky counts grapheme clusters under a separate 3,000-byte ceiling — so the split matches what the API will accept rather than what a character count guesses.",
    networks: ["bluesky", "mastodon", "threads", "x"],
  },
  {
    key: "videoChecker",
    to: "/tools/video-checker",
    name: "Video format checker",
    answers: "Will this video upload?",
    blurb:
      "Drop a video and see which networks will take it. Codec, resolution, duration and whether the moov atom is at the front are read out of the file in your browser — the video is never uploaded anywhere — and each network's real rejection rules are applied to what it finds.",
    networks: ["bluesky", "instagram", "threads", "x", "youtube"],
  },
  {
    key: "mastodonLimits",
    to: "/tools/mastodon-limits",
    name: "Mastodon limit checker",
    answers: "What is my server's character limit?",
    blurb:
      "Mastodon's 500 is a default, not a rule — server admins change it, and plenty of instances run 1,000 or more. Enter any server and read what it actually enforces: characters, how it counts a link, how many attachments a post takes, and the alt-text cap.",
    networks: ["mastodon"],
  },
  {
    key: "blueskyEngagementRate",
    to: "/tools/bluesky-engagement-rate",
    name: "Bluesky engagement rate",
    answers: "What is this account's engagement rate?",
    blurb:
      "Works out any public account's engagement rate from its own recent posts. It reports the median rather than the mean, so a single post that took off cannot flatter the number into something the account does not usually get.",
    networks: ["bluesky"],
  },
  {
    key: "blueskyBestTime",
    to: "/tools/bluesky-best-time",
    name: "Bluesky best time to post",
    answers: "When should I post on Bluesky?",
    blurb:
      "Reads your own public posts and shows which hours and days did best for you specifically. When there are too few posts to draw a conclusion it says so, instead of dressing thin data up as a recommended slot.",
    networks: ["bluesky"],
  },
  {
    key: "mastodonBestTime",
    to: "/tools/mastodon-best-time",
    name: "Mastodon posting-time analyser",
    answers: "When do my Mastodon posts do best?",
    blurb:
      "The same analysis for a fediverse account. It pages back through your public posts, buckets them by hour and weekday in your own timezone, and reports what the data supports rather than a generic “post at 9am”.",
    networks: ["mastodon"],
  },
] as const;
